import React from 'react';
import {
  TextField, Tooltip, tooltipClasses, Box, alpha,
} from '@mui/material';
import { styled } from '@mui/material/styles';

interface StyledProps {
  disabled?: boolean;
}
export const RangePickerTextField = styled(TextField)<StyledProps>(
  ({ theme, disabled }) => ({
    height: '100%',
    width: '100%',
    overflow: 'hidden',
    '& label': {
      left: theme.spacing(4),
      top: theme.spacing(-0.3),
      fontSize: theme.spacing(2),
      color: disabled ? theme.palette.action.disabled : theme.palette.common.white,
      width: '80%',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      [theme.breakpoints.down('md')]: {
        top: theme.typography.pxToRem(7),
      },
    },
    '& label.MuiInputLabel-shrink': {
      display: 'none',
    },
    '& .MuiInputBase-root': {
      backgroundColor: 'transparent',
      fontSize: theme.typography.pxToRem(14),
      height: '100%',
      borderWidth: 2,
      bottom: -2,
      caretColor: 'transparent',
      '&::before, after': {
        borderColor: 'transparent !important',
      },
    },
    '& .MuiInputBase-input': {
      width: theme.spacing(18.5),
      padding: theme.spacing(1.625, 3, 1.125, 6),
      fontSize: theme.spacing(2),
      height: '100%',
      [theme.breakpoints.between('lg', 'xl')]: {
        padding: theme.spacing(1.75, 2, 1.5, 5),
      },
    },
    '& fieldset': {
      border: 'none',
    },
  }),
);
export const StyledTooltip = styled(
  ({ className, ...props }: React.ComponentProps<typeof Tooltip> & { className?: string }) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
  ),
)(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.background.secondary,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.background.secondary,
  },
  '& .MuiAlert-root': {
    backgroundColor: theme.palette.background.secondary,
    color: theme.palette.common.fogWhite,
    fontSize: theme.typography.pxToRem(12),
    width: theme.typography.pxToRem(190),
    padding: 0,
    '& .MuiAlert-icon': {
      color: theme.palette.common.white,
    },
  },
}));
const StyledBox = styled(Box)(({ theme }) => ({

  height: '100%',
  '& .GRPTooltipButton': {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    cursor: 'not-allowed',
    '& .MuiTouchRipple-root': {
      display: 'none',
    },
  },
  '& .GRPCloseButton': {
    position: 'absolute',
    right: theme.spacing(-0.4),
    top: '50%',
    transform: 'translateY(-50%)',
    '& svg': {
      width: theme.spacing(2),
      height: theme.spacing(2),
    },
  },
  '& .rmdp-container ': {
    height: '100% !important',
  },
  '& .rmdp-wrapper': {
    background: theme.palette.background.dark,
    border: `1px solid ${theme.palette.divider[7]}`,
    borderRadius: theme.spacing(1),
    position: 'relative',
    backdropFilter: `blur(${theme.spacing(3)})`,
    WebkitBackdropFilter: `blur(${theme.spacing(3)})`,
    boxShadow: 'none !important',
    color: theme.palette.common.white,
    '&.single-day': {
      '& .rmdp-range': {
        backgroundColor: `${theme.palette.primary.main} !important`,
        color: `${theme.palette.common.black} !important`,
        boxShadow: 'unset',
        borderRadius: '50% !important',
        '&:hover': {
          backgroundColor: `${theme.palette.primary.main} !important`,
          color: `${theme.palette.common.black} !important`,
          boxShadow: 'unset',
          borderRadius: '50% !important',
        },
      },
    },
  },

  '& .rmdp-border-top': {
    borderTop: 0,
  },
  '& .rmdp-header': {
    marginTop: theme.typography.pxToRem(6),
  },
  '& .rmdp-header-values': {
    color: theme.palette.common.white,
    fontWeight: 400,
    fontSize: theme.typography.pxToRem(16),
  },
  '& .rmdp-week': {
    marginBottom: theme.spacing(0.5),
  },
  '& .rmdp-week-day': {
    color: theme.palette.common.white,
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 500,
  },
  '& .rmdp-range': {
    backgroundColor: alpha(theme.palette.primary.main, 0.15),
    color: theme.palette.common.white,
    boxShadow: 'unset',

    '&.start': {
      backgroundColor: theme.palette.primary.main,
      borderRadius: '50%',
      color: theme.palette.common.black,
      position: 'relative',
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        right: 0,
        width: '50%',
        height: '100%',
        backgroundColor: alpha(theme.palette.primary.main, 0.15),

      },
      '& span': {
        color: `${theme.palette.common.black} !important`,
      },
    },

    '&.end': {
      backgroundColor: theme.palette.primary.main,
      borderRadius: '50%',
      color: theme.palette.common.black,
      position: 'relative',
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '50%',
        height: '100%',
        backgroundColor: alpha(theme.palette.primary.main, 0.15),

      },
    },
    '&:not(.start):not(.end)': {
      backgroundColor: alpha(theme.palette.primary.main, 0.15),
      borderRadius: 0,
      color: theme.palette.common.white,
    },
  },

  '& .rmdp-ep-arrow': {
    '&[direction=top]': {
      borderBottom: 0,
    },

    '&:after': {
      backgroundColor: theme.palette.divider[7],
      boxShadow: 'none !important',
    },
  },
  '& .rmdp-day': {
    width: theme.typography.pxToRem(36),
    height: theme.typography.pxToRem(36),
    color: theme.palette.common.white,
    '&.rmdp-disabled': {
      color: theme.palette.action.disabled,
    },
    '&:not(.rmdp-day-hidden):not(.rmdp-range)': {
      '&:hover': {
        background: 'transparent',
        color: theme.palette.common.white,
        border: `1px solid ${theme.palette.common.white}`,
        borderRadius: '50%',
      },
    },
  },

  '& .rmdp-day.rmdp-today': {
    '& span': {
      background: 'transparent',
      // color: theme.palette.common.white,
      // border:`1px solid ${theme.palette.common.white}`,
      // borderRadius: '50% !important',
    },

  },
  '& .rmdp-arrow-container:hover': {
    backgroundColor: 'unset !important',
    boxShadow: 'unset !important',
    border: `1px solid ${theme.palette.common.white} !important`,
  },
  '& .rmdp-arrow': {
    borderColor: theme.palette.common.white,
  },
  '& .rmdp-right i, .rmdp-left i': {
    marginRight: 1,
    marginTop: theme.typography.pxToRem(6),
  },
  '& .rmdp-day:not(.rmdp-disabled):not(.rmdp-day-hidden) span:hover': {
    backgroundColor: 'transparent',
    color: theme.palette.common.white,
  },
  '& .rmdp-day-picker ': {
    padding: theme.typography.pxToRem(14),
    '& > div:first-of-type': {
      position: 'relative',
      [theme.breakpoints.up('md')]: {
        '&::after': {
          content: '""',
          position: 'absolute',
          top: '-20%',
          right: theme.typography.pxToRem(-5),
          width: theme.typography.pxToRem(1),
          height: '120%',
          backgroundColor: theme.palette.overlay[1],
          zIndex: 2,
        },
      },
    },
  },

  '& .rmdp-year-picker , .rmdp-month-picker': {
    background: theme.palette.secondary.dark,
    color: theme.palette.common.white,
    zIndex: 3,
    backdropFilter: `blur(${theme.spacing(5)})`,
    '& .rmdp-day': {
      '& span': {
        backgroundColor: 'transparent !important',
        boxShadow: 'none !important',
        color: theme.palette.common.white,
      },
      '&:hover:not(.rmdp-today):not(.rmdp-selected):not(.rmdp-disabled)': {
        borderRadius: theme.typography.pxToRem(100),
      },
      '&.rmdp-disabled': {
        fontWeight: '400 !important',
        '& span': {
          color: theme.palette.action.disabled,
        },
        '&:hover': {
          border: 0,
        },
      },
    },
    '& .rmdp-today': {
      borderRadius: theme.typography.pxToRem(100),
      '&:hover': {
        borderRadius: `${theme.typography.pxToRem(100)} !important`,
      },
    },
    '& .rmdp-selected': {
      borderRadius: theme.typography.pxToRem(100),
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.black,
      fontWeight: 400,
      boxShadow: 'none',
      borderColor: 'transparent !important',
      '& span': {
        color: `${theme.palette.common.black} !important`,
      },
      '&:hover': {
        borderRadius: `${theme.typography.pxToRem(100)} !important`,
        backgroundColor: `${theme.palette.primary.main} !important`,
        color: `${theme.palette.common.black} !important`,
        boxShadow: 'none !important',
        borderColor: 'transparent !important',
        '& span': {
          color: `${theme.palette.common.black} !important`,
          fontWeight: '400 !important',
        },
      },
    },
  },

  '& .rmdp-mobile': {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    borderRadius: 0,
    border: '0 !important',
    width: '100%',
    overflowX: 'hidden',

    '& .rmdp-week': {
      justifyContent: 'center',
    },
    '& .rmdp-day, .rmdp-week-day ': {
      [theme.breakpoints.down('md')]: {
        width: theme.spacing(6),
        height: theme.spacing(6),
      },
      '&.start span': {
        color: `${theme.palette.common.black} !important`,
      },
      '&.end span': {
        color: `${theme.palette.common.black} !important`,
      },
    },

    '& .rmdp-action-buttons': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing(2),
    },
    '& .rmdp-action-button': {
      width: '100%',
      height: theme.spacing(5.75),
      padding: theme.spacing(2),
      fontSize: theme.spacing(2),
      color: theme.palette.common.black,
      backgroundColor: theme.palette.primary.main,
      margin: 0,
      fontWeight: 600,
      borderRadius: theme.spacing(0.5),
      '&:first-child': {
        color: theme.palette.common.white,
        backgroundColor: 'unset',

      },
    },
    '& .rmdp-day-picker': {
      paddingTop: 4,
    },

    '& .rmdp-arrow': {
      padding: 4,
      width: 13,
      height: 13,
      margin: 0,
    },
    '& .rmdp-arrow-container': {
      margin: theme.spacing(0, 2),
      width: 60,
      height: 40,
      padding: theme.spacing(0, 0.75, 0, 0),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',

      '&.rmdp-left': {
        padding: theme.spacing(0, 0, 0, 0.75),
      },

    },

    '& .rmdp-header': {
      height: 'fit-content !important',
      paddingBottom: '0px !important',
    },
  },
}));

export default StyledBox;
